import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Space, notification } from "antd";
import { FlagOutlined } from "@ant-design/icons";
import service from "../../services/Service";
import { hasPrivilege } from "../../api/token";
import dayjs from 'dayjs';
import { withLocale } from "../../l10n";
import DataTable from "../DataTable/DataTable";
import Excelexport from "../../utils/excelexport";
import { formatDate, compareDates } from '../../utils/dates';
import { GetColumnLiteralSearchProps } from '../../utils/functions';
import { useGetVersionsLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function KiwiAssignedList ({ lang }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
  });

  const [data, setData] = useState({
    raw: [],
    selected: [],
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();

  // ===== 3. DERIVADOS =====
  const versionFilter = versions
    .filter(v => v.device_type === 'kiwi')
    .map(v => ({ text: v.name, value: v.uuid }));

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const fetchKiwis = useCallback(async () => {
    try {
      updateUI('loading', true);
      const res = await service.get("kiwi/manufacture/Asignado");

      if (res.status === 200) {
        updateData('raw', res.data.data || []);
      }
    } catch (err) {
      console.log("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  }, []);

  // Enriquecido derivado
  const enrichedData = useMemo(() => {
    return data.raw.map((element) => {
      const version = versions.find(v => v.uuid === element.version_uuid);
      return {
        ...element,
        version_name: version ? version.name : '-'
      };
    });
  }, [data.raw, versions]);

  // ===== 6. EFFECTS =====
  useEffect(() => {
    fetchKiwis();
  }, [fetchKiwis]);

  // ===== 7. HANDLERS =====
  const finishBoard = async (e) => {
    updateUI('loading', true);
    e.preventDefault();

    for (const id of data.selected.map(row => row.uuid)) {
      try {
        const res = await service.post("kiwi/finish/" + id);
        if (res.status === 200) {
          notification["success"]({
            message: lang.handle('KiWi terminado'),
          });
        }
      } catch {
        notification["error"]({
          message: lang.handle('No se ha podido terminar'),
        });
      }
    }

    updateData('selected', []);
    await fetchKiwis();
    updateUI('loading', false);
  };

  const getExportData = () => {
    return data.selected.map(row => ({
      UUID_BOX: row.board_uuid,
      SN: row.serial_number,
      MAC: row.mac,
      Conf_PSWD: "00#Kiconex",
      IP: "192.168.4.1",
      SSID: row.ssid,
      PASS: "12345678"
    }));
  }

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      updateData('selected', selectedRows);
    }
  };

  // ===== 8. CONFIG (columnas) =====
  const columns = [
    {
      title: lang.handle('Pedido'),
      dataIndex: "order_id",
      ...GetColumnLiteralSearchProps("order_id", lang),
    },
    {
      title: lang.handle('Número de serie'),
      dataIndex: "serial_number",
      ...GetColumnLiteralSearchProps("serial_number", lang),
    },
    {
      title: lang.handle('Orden de trabajo'),
      dataIndex: "work_order",
      ...GetColumnLiteralSearchProps("work_order", lang),
    },
    {
      title: "SSID",
      dataIndex: "ssid",
      ...GetColumnLiteralSearchProps("ssid", lang),
    },
    {
      title: "MAC",
      dataIndex: "mac",
      ...GetColumnLiteralSearchProps("mac", lang),
    },
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: "date",
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ["descend", "ascend"],
      render: (data) => formatDate(data),
    },
    {
      title: lang.handle('Versión'),
      dataIndex: "version_name",
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
      sorter: (a, b) => (a.version_name || '').localeCompare(b.version_name || '')
    },
    {
      title: lang.handle('UUID placa'),
      dataIndex: 'board_uuid',
      ...GetColumnLiteralSearchProps('board_uuid', lang),
    },
    {
      title: lang.handle('Notas'),
      dataIndex: "notes",
    },
    {
      title: lang.handle('Estado'),
      dataIndex: "state",
      filters: [
        { text: lang.handle('Fabricado'), value: 'Fabricado' },
        { text: lang.handle('Asignado'), value: 'Asignado' },
        { text: lang.handle('Terminado'), value: 'Terminado' },
      ],
      onFilter: (value, record) =>
        record.state && record.state.toLowerCase() === value.toLowerCase(),
    },
  ];

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        rowSelection={hasPrivilege('assign_finish') ? { type: 'checkbox', ...rowSelection } : null}
        columns={columns}
        rowKey="uuid"
        dataSource={enrichedData}
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('assign_finish') && <Button
            type="primary"
            icon={<FlagOutlined />}
            disabled={data.selected.length === 0}
            onClick={finishBoard}
          >
            {lang.handle('Terminar')}
          </Button>}
        </Space>
        <Space>
          <Excelexport
            fileName={'assigned_kiwi_' + dayjs().format('YYYYMMDD_HHmmss')}
            fileData={() => [{ name: 'kiwi_asignados', data: getExportData() }]}
            disabled={data.selected.length === 0}
            label={lang.handle('Exportar a Excel')}
          />
        </Space>
      </div>
    </div>
  );
}

export default withLocale(KiwiAssignedList);
